/* eslint-disable no-console */
'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  type Theme,
  type ThemeConfig,
  type ThemeMode,
  applyThemeToDocument,
  defaultThemeConfig,
  getAllThemes,
  getThemeById,
} from './theme-config';

function getSystemThemePreference(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

interface ThemeContextType {
  config: ThemeConfig;
  currentTheme: Theme;

  setTheme: (themeId: string) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;

  availableThemes: Theme[];
  availableModes: ThemeMode[];

  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultConfig?: ThemeConfig;
  themeStorageKey?: string;
  modeStorageKey?: string;
}

export function ThemeProvider({
  children,
  defaultConfig = defaultThemeConfig,
  themeStorageKey = 'ui-theme',
  modeStorageKey = 'ui-mode',
}: ThemeProviderProps) {
  const [config, setConfig] = useState<ThemeConfig>(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);

  const currentTheme = getThemeById(config.theme) || getThemeById(defaultConfig.theme)!;
  const availableThemes = getAllThemes();
  const availableModes = useMemo((): ThemeMode[] => ['light', 'dark'], []);

  useEffect(() => {
    try {
      const storedTheme = localStorage.getItem(themeStorageKey);
      const storedMode = localStorage.getItem(modeStorageKey) as ThemeMode;

      const systemPreference = getSystemThemePreference();

      const newConfig: ThemeConfig = {
        theme: storedTheme && getThemeById(storedTheme) ? storedTheme : defaultConfig.theme,
        mode: storedMode && availableModes.includes(storedMode) ? storedMode : systemPreference,
      };

      setConfig(newConfig);
      applyThemeToDocument(newConfig.theme, newConfig.mode);
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      const systemPreference = getSystemThemePreference();
      applyThemeToDocument(defaultConfig.theme, systemPreference);
    } finally {
      setIsLoading(false);
    }
  }, [defaultConfig, themeStorageKey, modeStorageKey, availableModes]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        const storedMode = localStorage.getItem(modeStorageKey);
        if (!storedMode) {
          const newMode = e.matches ? 'dark' : 'light';
          setConfig(prev => ({ ...prev, mode: newMode }));
        }
      };

      mediaQuery.addEventListener('change', handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }
  }, [modeStorageKey]);

  useEffect(() => {
    if (!isLoading) {
      applyThemeToDocument(config.theme, config.mode);
    }
  }, [config, isLoading]);

  const setTheme = (themeId: string) => {
    const theme = getThemeById(themeId);
    if (!theme) {
      console.warn(`Theme with id "${themeId}" not found`);
      return;
    }

    const newConfig = { ...config, theme: themeId };
    setConfig(newConfig);

    try {
      localStorage.setItem(themeStorageKey, themeId);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  const setMode = (mode: ThemeMode) => {
    const newConfig = { ...config, mode };
    setConfig(newConfig);

    try {
      localStorage.setItem(modeStorageKey, mode);
    } catch (error) {
      console.warn('Failed to save mode to localStorage:', error);
    }
  };

  const toggleMode = () => {
    const newMode = config.mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  };

  const value: ThemeContextType = {
    config,
    currentTheme,
    setTheme,
    setMode,
    toggleMode,
    availableThemes,
    availableModes,
    isLoading,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
